import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProviderId } from '@trivio/contracts';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { OnboardingLayout } from '../OnboardingLayout';
import { Alert } from '../../../components/ui/Alert';
import { LoadingState } from '../../../components/ui/Spinner';
import { listProviders, type ProviderConnection } from '../../../lib/api-client';
import { PROVIDER_CATALOG, ProviderConnectCard } from '../../../components/ProviderConnectCard';
import { useApiKeys } from '../ApiKeysContext';
import { useOnboardingState } from '../useOnboardingState';

type ConnectionsState = Partial<Record<ProviderId, ProviderConnection>>;

/**
 * Tela 3 — Provedores (/onboarding/providers). Every `PUT
 * /v1/providers/{provider}/credentials` call here MUST use the `trv_live_`
 * key — it's the lojista's real credential and needs real Secret Manager
 * persistence (see CnpjStep.tsx's docstring for why the `trv_test_` key
 * would silently no-op this call instead).
 */
export function ProvidersStep() {
  const { keys, loading: keysLoading, error: keysError } = useApiKeys();
  const { update } = useOnboardingState();
  const navigate = useNavigate();

  const [connections, setConnections] = useState<ConnectionsState>({});
  const [prefillLoading, setPrefillLoading] = useState(true);

  useEffect(() => {
    if (!keys.live) {
      setPrefillLoading(false);
      return;
    }
    let cancelled = false;
    listProviders(keys.live.key)
      .then((list) => {
        if (cancelled) return;
        const next: ConnectionsState = {};
        for (const connection of list) {
          next[connection.provider] = connection;
        }
        setConnections(next);
      })
      .catch(() => {
        // Best-effort prefill only — an empty starting state is still correct, just less convenient on a revisit.
      })
      .finally(() => {
        if (!cancelled) setPrefillLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [keys.live]);

  const activeProviderCount = Object.values(connections).filter((c) => c?.status === 'active').length;
  const hasActiveConnection = activeProviderCount > 0;

  function goToDemo() {
    const connectedProviders = Object.values(connections)
      .filter((c): c is ProviderConnection => c?.status === 'active')
      .map((c) => c.provider);
    update({ step: 'demo', connectedProviders });
    navigate('/onboarding/demo');
  }

  if (keysLoading) {
    return (
      <OnboardingLayout step="providers" title="Conecte um provedor de frete">
        <LoadingState label="Preparando suas chaves de API…" />
      </OnboardingLayout>
    );
  }

  if (keysError || !keys.live) {
    return (
      <OnboardingLayout step="providers" title="Conecte um provedor de frete">
        <Alert variant="error" title="Não foi possível preparar suas chaves de API">
          {keysError ?? 'Tente recarregar a página.'}
        </Alert>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      step="providers"
      title="Conecte um provedor de frete"
      description="Use suas próprias credenciais de cada provedor — o Trivio nunca intermedia o pagamento do frete, só orquestra a cotação e o rastreio."
    >
      {prefillLoading ? <LoadingState label="Verificando provedores já conectados…" /> : null}

      <div className="flex flex-col gap-4">
        {PROVIDER_CATALOG.map((provider) => (
          <ProviderConnectCard
            key={provider.id}
            config={provider}
            apiKey={keys.live!.key}
            connection={connections[provider.id] ?? null}
            onConnected={(connection) =>
              setConnections((prev) => ({ ...prev, [provider.id]: connection }))
            }
          />
        ))}
      </div>

      <div className="flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="outline" onClick={goToDemo}>
          Pular por enquanto (usar sandbox)
        </Button>
        <Button type="button" variant="default" disabled={!hasActiveConnection} onClick={goToDemo}>
          Continuar com {activeProviderCount || 1} provedor{activeProviderCount > 1 ? 'es' : ''}
        </Button>
      </div>
    </OnboardingLayout>
  );
}
